/**
 * API client for applications and the asset to application mapping
 * (apps/api-controller/src/modules/discovery/applications/handlers.ts, slice
 * D1). Reads need discovery:read; writes need discovery:applications:write
 * (DPO, Compliance Analyst). Row-level security limits a unit-scoped user to
 * their units' applications, assets and mappings and the tenant-wide ones, so
 * the page shows what the API returns.
 */

import { apiFetch } from './api';

export type ApplicationSource = 'foctta_core' | 'local';
export type ApplicationStatus = 'active' | 'retired';
export type LinkScope = 'data_source' | 'asset';

export interface Owner {
  name: string | null;
  email: string | null;
}

export interface Application {
  id: string;
  source: ApplicationSource;
  /** FOCTTA Consent's application id, for an application that came from Consent. */
  coreRef: string | null;
  name: string;
  description: string | null;
  businessUnitId: string | null;
  coreBusinessUnitRef: string | null;
  applicationOwner: Owner | null;
  dataOwner: Owner | null;
  status: ApplicationStatus;
  dataSourceCount: number;
  assetOverrideCount: number;
  createdAt: string;
  updatedAt: string;
  retiredAt: string | null;
}

export interface DataSource {
  dataSourceKey: string;
  sourceType: string;
  assetCount: number;
  overrideCount: number;
  link: {
    id: string;
    applicationId: string;
    applicationName: string | null;
    applicationStatus: ApplicationStatus | null;
    linkSource: 'operator' | 'suggested';
    /** The unit whose assets the link decides; null means every asset of the data source. */
    businessUnitId: string | null;
    updatedAt: string;
  } | null;
}

export interface DataSourceAsset {
  assetId: string;
  displayName: string;
  sourceType: string;
  ref: string | null;
  businessUnitId: string | null;
  sensitivity: string;
  override: { linkId: string; applicationId: string; applicationName: string | null } | null;
  effective: { applicationId: string; applicationName: string | null; via: LinkScope } | null;
}

export interface ApplicationFields {
  name?: string;
  description?: string | null;
  businessUnitId?: string | null;
  applicationOwnerName?: string | null;
  applicationOwnerEmail?: string | null;
  dataOwnerName?: string | null;
  dataOwnerEmail?: string | null;
  status?: ApplicationStatus;
}

/** The routes' own page ceiling. */
const PAGE = 500;
/** 5 000 rows is past any real inventory; stop rather than loop on a bad total. */
const MAX_PAGES = 10;

async function allPages<T>(path: string, params: Record<string, string>): Promise<T[]> {
  const out: T[] = [];
  for (let page = 0; page < MAX_PAGES; page++) {
    const qs = new URLSearchParams({ ...params, limit: String(PAGE), offset: String(page * PAGE) });
    const res = await apiFetch<{ items: T[]; total: number }>(`${path}?${qs}`);
    out.push(...res.items);
    if (res.items.length < PAGE || out.length >= res.total) break;
  }
  return out;
}

export function listApplications(status: ApplicationStatus | 'all' = 'all'): Promise<Application[]> {
  return allPages<Application>('/v1/discovery/applications', { status });
}

export function listDataSources(): Promise<DataSource[]> {
  return allPages<DataSource>('/v1/discovery/application-data-sources', {});
}

export function listDataSourceAssets(dataSourceKey: string): Promise<DataSourceAsset[]> {
  return allPages<DataSourceAsset>('/v1/discovery/application-data-sources/assets', { dataSourceKey });
}

export async function createApplication(fields: ApplicationFields & { name: string }): Promise<Application> {
  const res = await apiFetch<{ application: Application }>('/v1/discovery/applications', {
    method: 'POST',
    body: JSON.stringify(fields),
  });
  return res.application;
}

export async function updateApplication(id: string, fields: ApplicationFields): Promise<Application> {
  const res = await apiFetch<{ application: Application }>(`/v1/discovery/applications/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(fields),
  });
  return res.application;
}

export type LinkTarget = { scope: 'data_source'; dataSourceKey: string } | { scope: 'asset'; assetId: string };

export async function setLink(target: LinkTarget, applicationId: string): Promise<{ assetCount: number | null }> {
  return apiFetch<{ assetCount: number | null }>('/v1/discovery/application-links', {
    method: 'PUT',
    body: JSON.stringify({ ...target, applicationId }),
  });
}

export async function removeLink(linkId: string): Promise<void> {
  await apiFetch(`/v1/discovery/application-links/${encodeURIComponent(linkId)}`, { method: 'DELETE' });
}
